import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X } from 'lucide-react';
import type { GistPhotoView } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { ApiPicture } from '../lib/api-picture';
import { useOverlay } from '../lib/back-stack';
import { takeSnapshot } from '../lib/camera';

// The in-game camera (decided with Jerry, 28 October): Gist steps aside to show the game as it is (the map, the
// place you are in, your home); move or zoom, then the shutter; Retake or Use photo, and back to the post. Only
// the game's own picture, never the device's camera.
export function GistCamera({ onPhoto, onClose }: { onPhoto: (photo: GistPhotoView) => void; onClose: () => void }) {
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Gist (and the phone over the game) stand aside while the camera is up
  useEffect(() => {
    document.body.classList.add('gist-camera-on');
    return () => document.body.classList.remove('gist-camera-on');
  }, []);
  useEffect(() => () => void (shot && URL.revokeObjectURL(shot.url)), [shot]);
  // back or Escape closes the camera, not the post behind it
  useOverlay(true, onClose);
  const take = async () => {
    setBusy(true);
    const blob = await takeSnapshot().catch(() => null);
    setBusy(false);
    if (!blob) return notify('The camera could not see the game. Try again.');
    setShot({ blob, url: URL.createObjectURL(blob) });
  };
  const use = async () => {
    if (!shot) return;
    setBusy(true);
    try {
      onPhoto((await api.gist.photo(shot.blob)).photo);
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    shot ? (
      <div className="gist-camera preview" role="dialog" aria-modal="true" aria-label="Your photo">
        <img src={shot.url} alt="The photo you took" />
        <div className="gist-camera-bar">
          <button type="button" className="gist-cancel" disabled={busy} onClick={() => setShot(null)}>
            Retake
          </button>
          <button type="button" className="gist-post-button" disabled={busy} onClick={() => void use()}>
            {busy ? 'Sending…' : 'Use photo'}
          </button>
        </div>
      </div>
    ) : (
      <div className="gist-camera" role="dialog" aria-label="Camera">
        <span className="gist-camera-frame" aria-hidden="true" />
        <div className="gist-camera-bar">
          <button type="button" className="gist-camera-close" aria-label="Close the camera" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
          <button
            type="button"
            className="gist-shutter"
            aria-label="Take the photo"
            disabled={busy}
            onClick={() => void take()}
          >
            <Camera aria-hidden="true" />
          </button>
          <small>Move or zoom the game, then take the photo</small>
        </div>
      </div>
    ),
    document.body,
  );
}

// a photo on a post: tap to see it whole; while it waits for a moderator only its poster sees it, marked so
export function GistPhoto({ photo }: { photo: GistPhotoView }) {
  const [open, setOpen] = useState(false);
  // back or Escape closes the full view, not Gist behind it
  useOverlay(open, () => setOpen(false));
  return (
    <>
      <button
        type="button"
        className="gist-photo"
        aria-label="Open the photo"
        style={{ aspectRatio: `${photo.width} / ${photo.height}` }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <ApiPicture path={photo.src} alt="A photo from the game" />
      </button>
      {photo.checking && <p className="gist-held">⏳ Checking your photo… only you can see it</p>}
      {open &&
        createPortal(
          <div
            className="gist-photo-full"
            role="dialog"
            aria-modal="true"
            aria-label="Photo"
            onClick={(event) => {
              // a portal's clicks still reach the post card behind it: stop them here
              event.stopPropagation();
              setOpen(false);
            }}
          >
            <ApiPicture path={photo.src} alt="A photo from the game" />
            <button type="button" aria-label="Close the photo">
              <X aria-hidden="true" />
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
